import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { CalendarIcon, Clock, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useAuth } from "@/components/AuthProvider";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { reportsService } from "@/services/reportsService";
import { useClientsQuery } from "@/hooks/useClientsQuery";

// Form validation schema for research activities
const researchActivitySchema = z.object({
  project_id: z.string().optional(),
  report_id: z.string().optional(),
  duration_minutes: z.number().min(1, "Duration must be at least 1 minute"),
  work_type: z.string().min(1, "Please select what you worked on"),
  other_description: z.string().optional(),
  activity_date: z.date(),
}).refine(
  (data) => data.project_id || data.report_id,
  {
    message: "Please select either a project or a wisdrop report",
    path: ["project_id"]
  }
).refine(
  (data) => data.work_type !== "Other" || data.other_description,
  {
    message: "Please describe your work when selecting 'Other'",
    path: ["other_description"]
  }
);

type ResearchActivityFormData = z.infer<typeof researchActivitySchema>;

interface ResearchActivityFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projects: Array<{ id: string; name: string; clients?: { name: string } }>;
  editingActivity?: any;
  onEditComplete?: () => void;
}

export function ResearchActivityForm({ open, onOpenChange, projects, editingActivity, onEditComplete }: ResearchActivityFormProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedClient, setSelectedClient] = useState<string>("all");

  // Fetch clients
  const { data: clients = [] } = useClientsQuery();

  // Fetch wisdrop reports
  const { data: reports = [] } = useQuery({
    queryKey: ['reports'],
    queryFn: () => reportsService.getReports(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Filter projects based on selected client
  const filteredProjects = selectedClient === "all" 
    ? projects 
    : projects.filter(project => {
        const projectClientId = (project as any).client_id;
        return projectClientId === selectedClient;
      });

  const form = useForm<ResearchActivityFormData>({
    resolver: zodResolver(researchActivitySchema),
    defaultValues: {
      activity_date: new Date(),
      duration_minutes: 60,
      work_type: "",
      other_description: "",
    },
  });

  // Set form values when editing
  useEffect(() => {
    if (editingActivity) {
      form.reset({
        project_id: editingActivity.project_id || "",
        report_id: editingActivity.report_id || "",
        duration_minutes: editingActivity.duration_minutes || 60,
        work_type: editingActivity.work_type || "",
        other_description: editingActivity.other_description || "",
        activity_date: editingActivity.activity_date ? new Date(editingActivity.activity_date) : new Date(),
      });
    } else {
      form.reset({
        activity_date: new Date(),
        duration_minutes: 60,
        work_type: "",
        other_description: "",
      });
    }
  }, [editingActivity, form]);

  const workType = form.watch("work_type");

  const createResearchActivityMutation = useMutation({
    mutationFn: async (data: ResearchActivityFormData) => {
      // Get the profile ID from the profiles table using the auth user ID
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user?.id)
        .single();

      if (profileError) throw profileError;

      // Determine status based on activity date
      const activityDate = new Date(data.activity_date);
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today
      const status = activityDate <= today ? "completed" : "Draft";

      if (editingActivity) {
        // Update existing activity
        const { error } = await supabase
          .from("activities")
          .update({
            project_id: data.project_id || null,
            report_id: data.report_id || null,
            duration_minutes: data.duration_minutes,
            work_type: data.work_type,
            other_description: data.work_type === "Other" ? data.other_description : null,
            description: data.work_type === "Other" ? data.other_description : data.work_type,
            activity_date: data.activity_date.toISOString(),
            status: status,
          })
          .eq("id", editingActivity.id);

        if (error) throw error;
      } else {
        // Create new activity
        const { error } = await supabase
          .from("activities")
          .insert({
            type: "research",
            project_id: data.project_id || null,
            report_id: data.report_id || null,
            duration_minutes: data.duration_minutes,
            work_type: data.work_type,
            other_description: data.work_type === "Other" ? data.other_description : null,
            description: data.work_type === "Other" ? data.other_description : data.work_type,
            activity_date: data.activity_date.toISOString(),
            created_by: profile.id, // Use profile ID instead of user ID
            subject: "Research Activity",
            status: status,
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      toast({
        title: "Success",
        description: editingActivity ? "Research activity updated successfully" : "Research activity logged successfully",
      });
      form.reset();
      if (editingActivity && onEditComplete) {
        onEditComplete();
      } else {
        onOpenChange(false);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || `Failed to ${editingActivity ? 'update' : 'log'} research activity`,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: ResearchActivityFormData) => {
    createResearchActivityMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingActivity ? 'Edit Research Activity' : 'Log Research Activity'}</DialogTitle>
        </DialogHeader>
        {/* Updated form with Wisdrop Reports and predefined work types */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Client Filter */}
            <div className="space-y-2">
              <Label>Client Filter (Optional)</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger className="bg-background">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients?.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <FormField
              control={form.control}
              name="project_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="Select project (optional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-background z-50">
                      {filteredProjects?.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.name} {project.clients?.name && `(${project.clients.name})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="report_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Wisdrop Reports</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="Select wisdrop report (optional)" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-background z-50">
                      {reports?.map((report) => (
                        <SelectItem key={report.id} value={report.id}>
                          {report.name} S{report.season} ({report.category})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="duration_minutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Duration (minutes)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="1"
                      placeholder="60"
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="work_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What did you work on?</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="Select work type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-background z-50">
                      <SelectItem value="Customer Feedback">Customer Feedback</SelectItem>
                      <SelectItem value="Design">Design</SelectItem>
                      <SelectItem value="Analysis">Analysis</SelectItem>
                      <SelectItem value="Reporting">Reporting</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {workType === "Other" && (
              <FormField
                control={form.control}
                name="other_description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Describe your work</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Describe the research activities, findings, or tasks completed..."
                        className="min-h-[100px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="activity_date"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Date</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value ? (
                            format(field.value, "PPP")
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) =>
                          date > new Date() || date < new Date("1900-01-01")
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-3 justify-end pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (editingActivity && onEditComplete) {
                    onEditComplete();
                  } else {
                    onOpenChange(false);
                  }
                }}
                disabled={createResearchActivityMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createResearchActivityMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {createResearchActivityMutation.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {editingActivity ? 'Update Activity' : 'Log Activity'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}